import { m } from 'framer-motion';
import { useMemo, useState, useEffect } from 'react';

import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import SvgIcon from '@mui/material/SvgIcon';
import IconButton from '@mui/material/IconButton';

import { useBoolean } from 'src/hooks/use-boolean';

import { useGetUserScheduleEvents } from 'src/modules/profile/services';
import { useNotificationHubContext } from 'src/contexts/notification-context';

import { Label } from 'src/components/label';
import { varHover } from 'src/components/animate';
import { CustomTabs } from 'src/components/custom-tabs';

import { EventsContent } from './events/events-content';
import { NotificationsContent } from './notifications/notifications-content';

// ----------------------------------------------------------------------

// LocalStorage keys for notifications read status
const EVENTS_READ_KEY = 'events-read-status';

// ----------------------------------------------------------------------

export function NotificationsDrawer({ sx, ...other }) {
  const drawer = useBoolean();
  const [storageKey, setStorageKey] = useState(0); // Force re-render when localStorage changes
  const [mainTab, setMainTab] = useState('notifications');

  const twoDaysAgoUtc = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const fiveDaysLaterUtc = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const { scheduleEvents } = useGetUserScheduleEvents(twoDaysAgoUtc, fiveDaysLaterUtc);

  // SignalR connection for notifications
  const { notifications: signalRNotifications, unreadCount: signalRNotificationCount } =
    useNotificationHubContext();

  // Listen to localStorage changes (only for events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === EVENTS_READ_KEY) {
        setStorageKey((prev) => prev + 1);
      }
    };

    // Listen to storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Listen to custom event for same-tab changes
    const handleCustomStorageChange = () => {
      setStorageKey((prev) => prev + 1);
    };
    window.addEventListener('localStorageUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdated', handleCustomStorageChange);
    };
  }, []);

  // Calculate unread count (Events from localStorage + Notifications from backend)
  const unreadCount = useMemo(() => {
    let totalCount = 0;

    try {
      // Reference storageKey so it's counted as used
      const _ = storageKey;

      // Count Events from localStorage
      if (scheduleEvents && scheduleEvents.length > 0) {
        const storedReadStatus = localStorage.getItem(EVENTS_READ_KEY);
        const readStatusMap = storedReadStatus ? JSON.parse(storedReadStatus) : {};

        const eventsCount = scheduleEvents.filter((event) => {
          const hasStoredValue = readStatusMap[event.Id] !== undefined;
          // If stored: use the stored value, if not: default to false (unread)
          const isRead = hasStoredValue ? readStatusMap[event.Id] : false;
          return !isRead;
        }).length;

        totalCount += eventsCount;
      }

      // Count Notifications from backend IsRead property (NO localStorage)
      if (signalRNotifications && signalRNotifications.length > 0) {
        totalCount += signalRNotificationCount;
      }

      return totalCount;
    } catch (error) {
      console.error('Error calculating unread count:', error);
      return 0;
    }
  }, [scheduleEvents, signalRNotifications, signalRNotificationCount, storageKey]);

  // Count unread for each tab
  const notificationsUnreadCount = useMemo(() => {
    if (!signalRNotifications || signalRNotifications.length === 0) return 0;
    return signalRNotifications.filter((n) => !n.isRead).length;
  }, [signalRNotifications]);

  const eventsUnreadCount = useMemo(() => {
    const _ = storageKey; // Reference storageKey
    if (!scheduleEvents || scheduleEvents.length === 0) return 0;

    try {
      const storedReadStatus = localStorage.getItem(EVENTS_READ_KEY);
      const readStatusMap = storedReadStatus ? JSON.parse(storedReadStatus) : {};

      return scheduleEvents.filter((event) => {
        const hasStoredValue = readStatusMap[event.Id] !== undefined;
        const isRead = hasStoredValue ? readStatusMap[event.Id] : false;
        return !isRead;
      }).length;
    } catch (error) {
      return 0;
    }
  }, [scheduleEvents, storageKey]);

  const handleChangeMainTab = (event, newValue) => {
    setMainTab(newValue);
  };

  const renderMainTabs = (
    <CustomTabs variant="fullWidth" value={mainTab} onChange={handleChangeMainTab}>
      <Tab
        value="notifications"
        label="Notifications"
        iconPosition="end"
        icon={
          notificationsUnreadCount > 0 ? (
            <Label variant="filled" color="error">
              {notificationsUnreadCount}
            </Label>
          ) : null
        }
      />
      <Tab
        value="events"
        label="Events"
        iconPosition="end"
        icon={
          eventsUnreadCount > 0 ? (
            <Label variant="filled" color="info">
              {eventsUnreadCount}
            </Label>
          ) : null
        }
      />
    </CustomTabs>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={unreadCount} color="error">
          <SvgIcon>
            {/* https://icon-sets.iconify.design/solar/bell-bing-bold-duotone/ */}
            <path
              fill="currentColor"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="currentColor"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderMainTabs}

        {mainTab === 'notifications' && <NotificationsContent />}

        {mainTab === 'events' && <EventsContent />}
      </Drawer>
    </>
  );
}
